// Copyright (c) 2025 TrustCloud Corporation - All rights reserved.
//
// This software contains valuable confidential and proprietary information of
// TrustCloud Corporation
//
// Unauthorized use, reproduction, transmission, or distribution of this file and its
// contents is a violation of applicable law.

import React, { useMemo } from 'react';
import { IconButton, Tag, Typography } from '@kintent/glide';
import { format, parseISO } from 'date-fns';
import { UilBell } from '@iconscout/react-unicons';
import { Link, useCurrentRoute } from 'react-navi';

import * as Styles from './EnterpriseNotifications.styles';
import { Helmet } from '../../../components/Helmet';
import { NOTIFICATION_TYPE_COLOR_VARIANTS, TRUSTSHARE_NOTIFICATION_TYPE_LABELS } from '../../../lib/constants';
import { useTrustShareNotifications } from '../../../lib/state';

// Constants
const PAGE_TITLE = 'Notifications';

/**
 * This constant is used so we do not need to hard code the url params in the loop
 * For example, the type filter would be <placeholder.com>/notifications?type=type
 * and the date filter would be <placeholder.com>/notifications?date=date
 */
const NOTIFICATION_FILTER_SEARCH_PARAM = {
  TYPE: '?type',
  DATE: '?date',
};

function groupNotificationsByType(notifications) {
  return (
    notifications?.reduce((acc, notification) => {
      const { type } = notification;
      if (!acc.has(type)) {
        acc.set(type, [notification]);
        return acc;
      }
      acc.set(type, [...acc.get(type), notification]);
      return acc;
    }, new Map()) ?? new Map()
  );
}

function groupNotificationsByDate(notifications) {
  return (
    notifications?.reduce((acc, notification) => {
      const { _metadata } = notification;
      const createdDate = parseISO(_metadata.createdDate);

      const key = format(createdDate, 'yyyy-MM');
      const label = format(createdDate, 'MMMM yyyy');

      if (!acc.has(key)) {
        acc.set(key, {
          label,
          notifications: [notification],
        });
        return acc;
      }

      acc.set(key, {
        ...acc.get(key),
        notifications: [...acc.get(key).notifications, notification],
      });
      return acc;
    }, new Map()) ?? new Map()
  );
}

function sortNotificationsByDate(notifications) {
  return notifications?.sort((a, b) => new Date(b._metadata.createdDate) - new Date(a._metadata.createdDate));
}

function LeftPanel() {
  const { url } = useCurrentRoute();

  const { data: allNotifications } = useTrustShareNotifications();

  const sortedNotifications = useMemo(() => sortNotificationsByDate(allNotifications), [allNotifications]);

  const notificationsByType = useMemo(() => groupNotificationsByType(sortedNotifications), [sortedNotifications]);
  const notificationByTypeIterator = Array.from(notificationsByType.entries()).sort();

  const notificationsByDate = groupNotificationsByDate(sortedNotifications);
  const notificationByDateIterator = Array.from(notificationsByDate.entries());

  const urlParams = new URLSearchParams(url.search);
  const notificationUrlParamForType = urlParams.get('type');
  const notificationUrlParamForDate = urlParams.get('date');

  // If no search params exist, we want to highlight that ”All” is selected
  const doesSearchParamExist = url.search?.trim().length > 0;

  return (
    <Styles.LeftPanel data-testid="notification-section-left-panel">
      <div className="notification-group-type-container">
        <Typography
          as={Link}
          level="8"
          className={doesSearchParamExist === false ? 'active' : null}
          href="/notifications"
        >
          All
        </Typography>
      </div>
      {notificationsByType.size > 0 && (
        <div className="notification-group-type-container">
          <Typography
            as="p"
            level="8"
            strength="60%"
          >
            Type
          </Typography>
          <ul>
            {notificationByTypeIterator.map(([type]) => {
              return (
                <li key={type}>
                  <Typography
                    as={Link}
                    level="8"
                    strength="100%"
                    className={notificationUrlParamForType === type ? 'active' : null}
                    href={`${NOTIFICATION_FILTER_SEARCH_PARAM.TYPE}=${type}`}
                  >
                    {TRUSTSHARE_NOTIFICATION_TYPE_LABELS[type]}
                  </Typography>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {notificationsByDate.size > 0 && (
        <div className="notification-group-type-container">
          <Typography
            as="p"
            level="8"
            strength="60%"
          >
            Date
          </Typography>
          <ul>
            {notificationByDateIterator.map(([key, { label }]) => {
              return (
                <li key={key}>
                  <Typography
                    as={Link}
                    level="8"
                    strength="100%"
                    className={notificationUrlParamForDate === key ? 'active' : null}
                    href={`${NOTIFICATION_FILTER_SEARCH_PARAM.DATE}=${key}`}
                  >
                    {label}
                  </Typography>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Styles.LeftPanel>
  );
}

function NotificationContentPanel() {
  const { data: allNotifications } = useTrustShareNotifications();
  const sortedNotifications = useMemo(() => sortNotificationsByDate(allNotifications), [allNotifications]);

  const notificationsByDate = useMemo(() => groupNotificationsByDate(sortedNotifications), [sortedNotifications]);
  const notificationByDateIterator = Array.from(notificationsByDate.entries());

  return (
    <Styles.NotificationContentPanel>
      {notificationByDateIterator.map(([key, { label, notifications }]) => {
        return (
          <NotificationItemWithSectionHeading
            key={key}
            label={label}
            notifications={notifications}
          />
        );
      })}
    </Styles.NotificationContentPanel>
  );
}

function NotificationItemWithSectionHeading({ label, notifications }) {
  const { url } = useCurrentRoute();

  const urlParams = new URLSearchParams(url.search);
  const notificationTypeParam = urlParams.get('type');
  const notificationDateParam = urlParams.get('date');

  const filteredNotifications = useMemo(() => {
    // Handle the case where we have the type url param
    if (notificationTypeParam != null) {
      return notifications.filter((notification) => notification.type === notificationTypeParam);
    }

    // Handle the case where we have the date url param
    if (notificationDateParam != null) {
      return notifications.filter((notification) => {
        const createdDate = new Date(notification._metadata.createdDate);
        const notificationYearMonth = format(createdDate, 'yyyy-MM');
        return notificationYearMonth === notificationDateParam;
      });
    }

    // If no type param is set, we want to show all notifications
    return notifications;
  }, [url.search, notifications]);

  // This check ensures we do not render a section with no notifications
  if (filteredNotifications.length === 0) return null;

  return (
    <div className="notification-item-section-container">
      <Typography
        as="p"
        level="7"
        strength="80%"
        className="notification-item-section-label"
      >
        {label}
      </Typography>
      {filteredNotifications?.map((notification) => {
        return (
          <NotificationItem
            key={notification.id}
            notification={notification}
          />
        );
      })}
    </div>
  );
}

function NotificationItem({ notification }) {
  const { id, title, description, type, _metadata } = notification;
  const displayDate = format(new Date(_metadata.createdDate), 'EEEE, MMMM d, yyyy');
  return (
    <Styles.NotificationCard className="notification-item-card">
      <Styles.Heading>
        <div className="notification-item-tag-container">
          <Tag
            type="category"
            variant={NOTIFICATION_TYPE_COLOR_VARIANTS[type]}
          >
            {TRUSTSHARE_NOTIFICATION_TYPE_LABELS[type]}
          </Tag>
        </div>
        <div className="notification-item-timestamp-container">
          <Typography level="8">{displayDate}</Typography>
        </div>
      </Styles.Heading>
      <Styles.Body>
        <Styles.Title>
          <Typography
            as="p"
            level="5"
            className="notification-item-title-copy"
          >
            {title}
          </Typography>
          <IconButton
            label="Subscribe"
            className="notification-item-icon-button"
            icon={<UilBell size="1em" />}
            data-testid="notification-subscribe-button"
          />
        </Styles.Title>
        <Typography
          as="p"
          level="8"
          strength="60%"
          dangerouslySetInnerHTML={{ __html: description }}
          className="notification-item-card-description"
        />
        <Typography
          as={Link}
          level="8"
          strength="60%"
          href={`/notifications/${id}`}
          className="notification-item-card-detail-view-link"
        >
          More Details
        </Typography>
      </Styles.Body>
    </Styles.NotificationCard>
  );
}

function EnterpriseNotifications() {
  return (
    <>
      <Helmet pageTitle={PAGE_TITLE} />
      <Styles.Wrapper>
        <Typography
          as="h1"
          level="1"
        >
          {PAGE_TITLE}
        </Typography>
        <section className="notification-section-grid">
          <LeftPanel />
          <NotificationContentPanel />
        </section>
      </Styles.Wrapper>
    </>
  );
}

export default EnterpriseNotifications;
